import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DialogTitle from 'components/DialogOverrides/DialogTitle';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  compareLabourEmployeeCostRatesImport,
  compareLabourEmployeeCostRatesUpdateImport,
  bulkUpdateLabourEmployeeCostRates,
  downloadLabourEmployeeCostRatesNewTemplate,
  downloadLabourEmployeeCostRatesUpdateTemplate,
  hasExistingEmployeeCostRates,
  BulkUpdateEmployeeCostRatesResponse,
  ImportMode,
} from 'api/labourApi';
import { handleMutationError } from 'util/handleMutationError';
import BulkUpdateSimpleRatesForm from './BulkUpdateSimpleRatesForm';
import { EmployeeCostResultView } from './LabourImportResultView';

interface Props {
  open: boolean;
  handleClose: () => void;
}

const TAB_LABEL: Record<ImportMode, string> = {
  new: 'Import New Cost Rate by Employee',
  update: 'Update Existing Cost Rate by Employee',
};

const TEMPLATE_FILENAME: Record<ImportMode, string> = {
  new: 'restorize_labour_rates_employee_new_template.xlsx',
  update: 'restorize_labour_rates_employee_update_template.xlsx',
};

const DESCRIPTION_TEXT: Record<ImportMode, string> = {
  new: "Download a template pre-filled with your active employees, set a first-time Cost Rate for the ones you want, then upload it back.",
  update: "Download a template pre-filled with your active employees' current default cost rates, edit the values you want to change, then upload it back.",
};

const INFO_TEXT: Record<ImportMode, string> = {
  new: "The template is pre-filled with a full list of active employees. The Employee Name and Role columns are read-only for reference.Cost Rate and Working Away From Home Cost Rate are both required to give the employee their first cost rate override, if Working Away From Home doesn't apply, enter the same value as Cost Rate. Effective Date is optional, leave it blank to use today's date, or provide one in YYYY-MM-DD format. Rows left blank are skipped. All changes are presented for review and are not saved until confirmed.",
  update: "The template is pre-filled with a full list of active employees. The Employee Name, Role, Current Cost Rate, Current Working Away From Home Cost Rate, and Current Cost Rate Effective From Date columns are read-only for reference. Enter a value in New Cost Rate and/or New Working Away From Home Cost Rate to update it, with a New Cost Rate Effective From Date (use YYYY-MM-DD format, leave blank to use today's date). If the employee has no active cost rate yet, both New values are required. All changes are presented for review and are not saved until confirmed.",
};

const EmployeeCostRatesTab = ({
  mode,
  onBusyChange,
  onClose,
}: {
  mode: ImportMode;
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkUpdateEmployeeCostRatesResponse | null>(null);
  const [result, setResult] = useState<BulkUpdateEmployeeCostRatesResponse | null>(null);
  const { data: hasExistingRates } = useQuery(['employee-cost-rates-exist'], hasExistingEmployeeCostRates, {
    enabled: mode === 'update',
  });
  const noExistingEmployeeData = mode === 'update' && hasExistingRates === false;

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update' ? compareLabourEmployeeCostRatesUpdateImport : compareLabourEmployeeCostRatesImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        enqueueSnackbar(`Failed to read the file: ${handleMutationError(error)}`, { variant: 'error' });
      },
    }
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(
    bulkUpdateLabourEmployeeCostRates,
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['labour-rates']);
        setResult(data);
        enqueueSnackbar(
          data.errorCount === 0
            ? 'Employee cost rates updated successfully'
            : `Employee cost rates updated — ${data.errorCount} row(s) skipped`,
          { variant: 'success' }
        );
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to update employee cost rates: ${handleMutationError(error)}`, {
          variant: 'error',
        });
      },
    }
  );

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update' ? downloadLabourEmployeeCostRatesUpdateTemplate : downloadLabourEmployeeCostRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(`Failed to download the template: ${handleMutationError(error)}`, {
          variant: 'error',
        });
      },
    }
  );

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';

  const resultDivergedFromPreview =
    !!preview &&
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  return (
    <>
      {step === 'form' && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <BulkUpdateSimpleRatesForm
              file={file}
              setFile={setFile}
              isLoading={isPreviewLoading}
              descriptionText={DESCRIPTION_TEXT[mode]}
              infoText={INFO_TEXT[mode]}
              templateFilename={TEMPLATE_FILENAME[mode]}
              onDownloadTemplate={() => downloadMutate()}
              isDownloadingTemplate={isDownloadingTemplate}
              blockedMessage={
                noExistingEmployeeData
                  ? 'Your organization doesn\'t have any existing employee cost rate overrides yet. Use the "Import New Cost Rate by Employee" tab to add some first.'
                  : undefined
              }
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={onClose} sx={{ color: colors.grey[600], mr: 1 }} disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file}
                endIcon={isPreviewLoading ? <CircularProgress size={15} /> : <ArrowForwardIcon />}
                onClick={() => {
                  if (!file) return;
                  previewMutate({ file });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <EmployeeCostResultView response={preview} />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={() => setPreview(null)} sx={{ color: colors.grey[600], mr: 1 }} disabled={isCommitLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  isCommitLoading ||
                  !preview.previewToken ||
                  (preview.createdCount === 0 && preview.updatedCount === 0)
                }
                endIcon={isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />}
                onClick={() => {
                  if (!preview.previewToken) return;
                  commitMutate({ previewToken: preview.previewToken });
                }}>
                {isCommitLoading
                  ? 'Please wait...'
                  : preview.errorCount > 0
                    ? 'Ignore Errors and Continue'
                    : 'Confirm Import'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'result' && result && (
        <>
          {resultDivergedFromPreview && (
            <Alert severity="warning" sx={{ m: 2 }}>
              The committed result differs from the preview you approved, data may have changed since
              preview. Review the details below.
            </Alert>
          )}
          <EmployeeCostResultView response={result} />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button variant="contained" color="primary" endIcon={<CheckIcon />} onClick={onClose}>
                Done
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </>
  );
};

const BulkUpdateEmployeeCostRatesDialog = ({ open, handleClose }: Props) => {
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<ImportMode>('new');

  const onClose = () => {
    if (isBusy) return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} disableEnforceFocus fullWidth scroll="paper" maxWidth="lg">
      <DialogTitle onClose={onClose}>Import Default Cost Rate by Employee</DialogTitle>
      <Divider />
      <Tabs
        value={mode}
        onChange={(_e, value) => {
          if (isBusy) return;
          setMode(value);
        }}
        variant="fullWidth">
        <Tab label={TAB_LABEL.new} value="new" disabled={isBusy} />
        <Tab label={TAB_LABEL.update} value="update" disabled={isBusy} />
      </Tabs>
      <Divider />

      <EmployeeCostRatesTab key={mode} mode={mode} onBusyChange={setIsBusy} onClose={onClose} />
    </Dialog>
  );
};

export default BulkUpdateEmployeeCostRatesDialog;
